/**
 * QUIET NUDGE — the text surface's half of the silence follow-up.
 *
 * Voice got it first (voice-bot silence.py, 2026-10-28, David: "can it
 * follow up if the user does not respond in a few seconds —
 * conversationally?"). Voice and text are two modalities of one system,
 * so the chat thread gets the same move: after the agent's reply, if the
 * visitor has said nothing for a reading-dwell, send ONE hidden turn and
 * let the agent carry the conversation forward in a line of its own
 * choosing — or call stayQuiet (registered on hidden turns) and send
 * nothing. The words are the model's; this module only marks the moment.
 *
 * Guards, mirroring silence.py:
 *  - once per visitor turn (a second silence after a nudge gets nothing);
 *  - a session cap, so an abandoned tab never grinds turns;
 *  - never on presenter mounts (presenter-stage owns that pacing), while a
 *    call is live, while narrated media or content has the floor, while
 *    the visitor is typing (a draft in the composer), outside the
 *    conversation state, or while the tab is hidden.
 *
 * The dwell scales with the reply: a visitor needs time to READ before
 * silence means anything (voice waits for the words to be heard).
 */

export type QuietNudgeDeps = {
  /** Send one hidden user turn (never rendered as a visitor bubble). */
  sendTick: (text: string) => void;
  /** Anything that means "busy, not quiet" right now — re-checked at fire time. */
  isBusy: () => boolean;
  /** Test seams; default to the real timers. */
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
};

/** Base dwell plus reading time for the reply (≈22 chars/s), bounded. */
export const NUDGE_BASE_MS = 6000;
export const NUDGE_PER_CHAR_MS = 45;
export const NUDGE_MIN_MS = 8000;
export const NUDGE_MAX_MS = 20000;
/** Nudges allowed per session without a visitor turn in between is 1; this caps the session. */
export const NUDGE_SESSION_CAP = 3;

export function nudgeDelayMs(replyChars: number): number {
  const ms = NUDGE_BASE_MS + Math.max(0, replyChars) * NUDGE_PER_CHAR_MS;
  return Math.min(NUDGE_MAX_MS, Math.max(NUDGE_MIN_MS, Math.round(ms)));
}

/** The hidden direction. An instruction, not a line — the model picks the words. */
export const NUDGE_TEXT =
  '(the visitor has been quiet for a moment after your last message. Carry the conversation forward in ONE short line — your choice of what fits: react to what they last said, offer to show one specific thing, or ask one different question. Never mention the silence, never repeat yourself, never re-ask the question you ended on, never re-greet. If there is genuinely nothing worth adding, call stayQuiet and send nothing.)';

export type QuietNudge = {
  /** A REAL visitor turn (typed message, chip tap): the slate is clean. */
  noteVisitorInput(): void;
  /** The agent's reply finished; `replyChars` sizes the reading dwell. */
  onTurnDone(replyChars: number): void;
  /** Cancel any pending nudge (voice starting, content opening…). */
  cancel(): void;
  dispose(): void;
};

export function createQuietNudge(deps: QuietNudgeDeps): QuietNudge {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));
  let timer: ReturnType<typeof setTimeout> | null = null;
  let nudgedThisTurn = false;
  let sentThisSession = 0;
  let disposed = false;

  const cancel = (): void => {
    if (timer != null) clearTimer(timer);
    timer = null;
  };

  return {
    noteVisitorInput(): void {
      nudgedThisTurn = false;
      cancel();
    },

    onTurnDone(replyChars: number): void {
      if (disposed) return;
      // The nudge's own reply (a hidden turn) also ends with `done`: the
      // once-per-visitor-turn rule is what keeps it from re-arming.
      if (nudgedThisTurn || sentThisSession >= NUDGE_SESSION_CAP) return;
      if (deps.isBusy()) return;
      cancel();
      timer = setTimer(() => {
        timer = null;
        if (disposed || nudgedThisTurn) return;
        // Re-check at fire time — they may have started typing or a call.
        if (deps.isBusy()) return;
        nudgedThisTurn = true;
        sentThisSession += 1;
        deps.sendTick(NUDGE_TEXT);
      }, nudgeDelayMs(replyChars));
    },

    cancel,

    dispose(): void {
      disposed = true;
      cancel();
    },
  };
}
